import { useState } from 'react';
import { MapPin, Clock, CheckCircle, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { projects, projectCategories } from '../projectsData';

export const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const getCategoryColor = (category) => {
    const colors = {
      'EICR': 'bg-blue-100 text-blue-700',
      'HMO Compliance': 'bg-purple-100 text-purple-700',
      'Consumer Unit': 'bg-green-100 text-green-700',
      'Rewiring': 'bg-orange-100 text-orange-700',
      'Commercial': 'bg-indigo-100 text-indigo-700',
      'Emergency Lighting': 'bg-yellow-100 text-yellow-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Our Projects</h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            See the quality electrical work we've completed for landlords, letting agents, and commercial clients across Greater London
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-blue-600">100+</p>
              <p className="text-sm text-gray-600">Happy Clients & Counting</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">100%</p>
              <p className="text-sm text-gray-600">NICEIC Certified</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">5.0★</p>
              <p className="text-sm text-gray-600">Client Satisfaction</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-blue-600">24-48hr</p>
              <p className="text-sm text-gray-600">Avg. Turnaround</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Trusted By
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto mb-12">
            We've delivered electrical works across commercial, education and hospitality environments including major national brands and institutions.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10 items-center">
            <div className="flex items-center justify-center">
              <img
                src="https://images.seeklogo.com/logo-png/48/1/costa-coffee-logo-png_seeklogo-481360.png"
                alt="Costa Coffee"
                className="max-h-11 w-auto grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/4/42/Tottenham_Hotspur_Stadium_logo.svg/960px-Tottenham_Hotspur_Stadium_logo.svg.png"
                alt="Tottenham Hotspur Stadium"
                className="max-h-11 w-auto grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://upload.wikimedia.org/wikipedia/sco/thumb/d/d1/University_College_London_logo.svg/960px-University_College_London_logo.svg.png"
                alt="UCL"
                className="max-h-11 w-auto grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://upload.wikimedia.org/wikipedia/en/thumb/1/1c/Harris_Science_Academy_East_London_logo.svg/500px-Harris_Science_Academy_East_London_logo.svg.png"
                alt="Harris Academy"
                className="max-h-11 w-auto grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://cdn.webdadi.net/Media/image/s/cf258f88-7d38-4c08-b839-82bd478254e3.png"
                alt="Star Estates"
                className="max-h-11 w-auto grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
            <div className="flex items-center justify-center">
              <img
                src="https://images.seeklogo.com/logo-png/38/1/sonder-logo-png_seeklogo-381749.png"
                alt="Sonder"
                className="max-h-11 w-auto grayscale opacity-75 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">
            Past Projects
          </h2>
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Filter by Category:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {projectCategories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.value)}
                className={selectedCategory === category.value ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredProjects.length}</span> {selectedCategory === 'all' ? 'projects' : `${selectedCategory} projects`}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden border-gray-200 hover:shadow-xl transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className={getCategoryColor(project.category)}>
                      {project.category}
                    </Badge>
                  </div>
                </div>
                
                <CardHeader>
                  <CardTitle className="text-xl">{project.title}</CardTitle>
                  <CardDescription className="flex flex-col gap-2 mt-2">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{project.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{project.duration} • {project.year}</span>
                    </div>
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <p className="text-gray-700 mb-4">{project.description}</p>
                  
                  <div className="space-y-2">
                    <p className="font-semibold text-gray-900 text-sm">Scope of Work:</p>
                    <ul className="space-y-1">
                      {project.scope.slice(0, 3).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                      {project.scope.length > 3 && (
                        <li className="text-sm text-blue-600 font-medium pl-6">
                          +{project.scope.length - 3} more items
                        </li>
                      )}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Get a free quote for your electrical compliance or installation project
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07711233685">
              <Button size="lg" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-gray-100">
                Call 07711 233685
              </Button>
            </a>
            <a href="https://wa.me/447711233685" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-blue-700">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
